import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  History,
  ShieldCheck,
  Search,
  Filter,
  ArrowRight,
  Clock,
  Calendar,
  AlertTriangle,
  FileText,
  MapPin,
  ExternalLink,
} from 'lucide-react';
import { NormalizedAlert } from '../types/alert';
import { AlertHistoryService } from '../services/alertHistoryService';
import { NWSAdapter } from '../services/nwsAdapter';
import { USGSAdapter } from '../services/usgsAdapter';
import { SeverityBadge } from '../components/common/SeverityBadge';
import { formatRelativeTime, formatFullDateTime } from '../utils/formatters';
import { AlertMap } from '../components/map/AlertMap';
import { AdSlot } from '../components/common/AdSlot';

export const HistoryPage: React.FC = () => {
  const [expiredAlerts, setExpiredAlerts] = useState<NormalizedAlert[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<NormalizedAlert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('ALL');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Fetch active data to update history records
        const [nws, usgs] = await Promise.allSettled([
          NWSAdapter.fetchActiveAlerts(),
          USGSAdapter.fetchEarthquakes(),
        ]);

        const active: NormalizedAlert[] = [];
        if (nws.status === 'fulfilled') active.push(...nws.value.alerts);
        if (usgs.status === 'fulfilled') active.push(...usgs.value.alerts);
        setActiveAlerts(active);

        // Record active to ensure expired detections are updated
        AlertHistoryService.recordAlerts(active);

        // Retrieve expired alerts
        const expired = AlertHistoryService.getExpiredAlerts();
        setExpiredAlerts(expired);
      } catch (e) {
        console.warn('Failed to refresh history:', e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filtered = useMemo(() => {
    return expiredAlerts.filter((a) => {
      if (selectedSeverity !== 'ALL' && a.severity !== selectedSeverity) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          a.event.toLowerCase().includes(q) ||
          a.areaDesc.toLowerCase().includes(q) ||
          a.senderName.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [expiredAlerts, searchQuery, selectedSeverity]);

  return (
    <div className="space-y-8 pb-16">
      {/* Header Banner */}
      <section className="bg-navy-950 text-white border-b border-slate-800 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-slate-300 text-xs font-mono">
            <History className="w-3.5 h-3.5 text-cyan-400" />
            <span>24-HOUR INCIDENT AUDIT ARCHIVE</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white font-sans">
            Cleared &amp; Expired Disruption Archive
          </h1>

          <p className="text-sm sm:text-base text-slate-300 max-w-3xl leading-relaxed font-sans">
            Review weather warnings, watches, and seismic events across the United States that expired or were resolved within the past 24 hours. Most official websites remove warnings the moment they expire; SignalNear maintains this 24-hour audit record for situational awareness, damage assessments, and community verification.
          </p>

          <div className="pt-2 flex flex-wrap gap-4 text-xs font-mono text-slate-400">
            <span>AUDIT RETENTION: 24 HOURS</span>
            <span>•</span>
            <span className="text-cyan-400">TRACKED CLEARED EVENTS: {expiredAlerts.length}</span>
            <span>•</span>
            <span>DATA INTEGRITY: UNALTERED OFFICIAL CAP LOGS</span>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Ad Slot */}
        <AdSlot slotId="history-top" format="horizontal" />

        {/* Search & Filters */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-grow">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search resolved storms by county, city, event name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-sans"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono text-slate-500 uppercase">Severity:</span>
              {['ALL', 'Extreme', 'Severe', 'Moderate', 'Minor'].map((sev) => (
                <button
                  key={sev}
                  onClick={() => setSelectedSeverity(sev)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    selectedSeverity === sev
                      ? 'bg-navy-950 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {sev}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* History Map View if expired events have coordinates */}
        {expiredAlerts.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-navy-950 font-sans flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span>Geospatial Map of Recently Cleared Zones</span>
              </h2>
              <span className="text-xs font-mono text-slate-500">
                Muted grey dashed polygons denote cleared warnings
              </span>
            </div>
            <AlertMap alerts={expiredAlerts} height="420px" showHistoryToggle={false} />
          </div>
        )}

        {/* Cleared Alerts List */}
        <div className="space-y-4">
          <h2 className="text-xl font-black text-navy-950 font-sans flex items-center justify-between">
            <span>Cleared Disruption Bulletins ({filtered.length})</span>
            <Link to="/" className="text-xs font-bold text-blue-600 hover:underline">
              Return to Live Radar &rarr;
            </Link>
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center space-y-3 max-w-lg mx-auto">
              <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto" />
              <h3 className="font-bold text-navy-950 text-base">No Expired Records in Past 24 Hours</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                As severe weather bulletins expire or are superseded by issuing weather offices, they are cataloged here automatically. Check back during active storm patterns.
              </p>
              <Link
                to="/"
                className="inline-block px-4 py-2 bg-navy-950 text-white text-xs font-bold rounded-lg hover:bg-navy-900"
              >
                Scan Live Radar
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((alert) => (
                <div
                  key={alert.id}
                  className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs hover:border-slate-300 transition-all space-y-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-200 text-slate-700 uppercase">
                        CLEARED
                      </span>
                      <SeverityBadge severity={alert.severity} size="sm" />
                      <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        {alert.sourceAgency}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-xs font-mono text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Expired: {formatRelativeTime(alert.expiredAt || alert.expires)}</span>
                      </span>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-black text-navy-950 text-base font-sans">
                      {alert.event}
                    </h3>
                    <p className="text-xs text-slate-600 font-sans mt-0.5">
                      <strong>Affected Regions:</strong> {alert.areaDesc}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="text-slate-500 font-mono text-[11px]">
                      Issued by: {alert.senderName} • Onset: {formatFullDateTime(alert.effective)}
                    </span>

                    <div className="flex items-center gap-3">
                      <Link
                        to={`/alert/${encodeURIComponent(alert.id)}`}
                        className="font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <span>Audit Record</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                      <a
                        href={alert.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[11px] text-slate-400 hover:text-slate-700 flex items-center gap-1"
                      >
                        <span>Source Link</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

